import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  INursingHomePhoenixApi,
  NursingHome,
  NursingHomeDetail,
  TenantInformation,
  UpdateNursingHomeBody,
} from './api';
import { NursingHomePhoenixApi } from './api';
import { getMaxReceiptBatchAmountInCents } from '@pflegenavi/shared/constants';
import type {
  CreateIdvSessionBody,
  FeatureFlag,
  IdvPsc,
  IdvSession,
  LegalEntity,
} from '@pflegenavi/shared/api';
import { useAuthentication } from '@pflegenavi/shared-frontend/authentication';
import { USER_PROFILE_KEY } from '../user-profile/queryKeys';

const ApiContext = createContext<INursingHomePhoenixApi | undefined>(undefined);
const {
  useApi: useNursingHomePhoenixApi,
  ApiProvider: NursingHomePhoenixApiProvider,
} = makeApiProvider({
  name: 'NursingHomePhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new NursingHomePhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useNursingHomePhoenixApi,
  NursingHomePhoenixApiProvider,
  ApiContext as NursingHomePhoenixApiContext,
};

export type { NursingHome, TenantInformation };

const LEGAL_ENTITIES_KEY: QueryKey = ['LEGAL_ENTITIES_KEY'];
const TENANT_INFORMATION_KEY: QueryKey = ['TENANT_INFORMATION_KEY'];
const NURSING_HOMES_KEY: QueryKey = ['NURSING_HOMES_KEY'];
const TENANT_FEATURE_FLAGS_KEY: QueryKey = ['TENANT_FEATURE_FLAGS_KEY'];
const LEGAL_ENTITY_IDV_SESSION_KEY = (legalEntityId: string): QueryKey => [
  'LEGAL_ENTITY_IDV_SESSION_KEY',
  legalEntityId,
];
export const NURSING_HOME_PHOENIX_KEY = (
  nursingHomeId: string | undefined
): QueryKey => ['NURSING_HOME_PHOENIX_KEY', nursingHomeId];

/**
 * Feature flags of the active tenant, for scopes where no nursing home is
 * selected. Within a nursing home read `selectedNursingHome.featureFlags`
 * instead, which already includes the tenant ones.
 *
 * Returns undefined outside apps that mount NursingHomePhoenixApiProvider.
 */
export const useGetTenantFeatureFlags = (): UseQueryResult<
  {
    data: {
      feature_flags: FeatureFlag[];
    };
  },
  unknown
> => {
  const api = useNursingHomePhoenixApi(true);

  return useApiQuery(
    () => api,
    TENANT_FEATURE_FLAGS_KEY,
    (api) => api!.getTenantFeatureFlags(),
    {
      staleTime: Infinity,
    }
  );
};

export const useGetPaymentProcessor = (): 'stripe' | 'mangopay' => {
  const auth = useAuthentication(true);
  return auth.paymentProcessor ?? 'stripe';
};

// Resolves the receipt-batch cap for the active tenant's payment processor.
// Prefer this over pairing useGetPaymentProcessor() with
// getMaxReceiptBatchAmountInCents() at each call site, so the processor->limit
// mapping lives in exactly one place.
export const useMaxReceiptBatchAmountInCents = (): number => {
  return getMaxReceiptBatchAmountInCents(useGetPaymentProcessor());
};

export const useGetTenantInformation = (): UseQueryResult<
  {
    data: TenantInformation;
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    TENANT_INFORMATION_KEY,
    (api) => {
      return api.getTenantInformation();
    },
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useGetNursingHomes = (
  options?: Omit<
    UseQueryOptions<
      {
        data: NursingHome[];
      },
      unknown,
      {
        data: NursingHome[];
      }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    data: NursingHome[];
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    NURSING_HOMES_KEY,
    (api) => {
      return api.getNursingHomes();
    },
    options
  );
};

// Nursing-home detail via Phoenix; replaces the legacy `useNursingHome`.
export const useNursingHomePhoenix = (
  nursingHomeId: string | undefined
): UseQueryResult<NursingHomeDetail, unknown> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    NURSING_HOME_PHOENIX_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw Error('nursingHomeId is undefined');
      }
      return api.getNursingHome({ params: { id: nursingHomeId } });
    },
    {
      enabled: nursingHomeId !== undefined,
      select: (result) => result.data,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnMount: false,
    }
  );
};

// Updates nursing-home detail and/or payment settings via Phoenix; replaces the
// legacy `useUpdateNursingHome` + `usePatchNursingHomePaymentSettings`.
export const useUpdateNursingHomePhoenix = (): UseMutationResult<
  NursingHomeDetail,
  unknown,
  { id: string; body: UpdateNursingHomeBody }
> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  const userId = api.authContext.user?.userId;

  return useMutation<
    NursingHomeDetail,
    unknown,
    { id: string; body: UpdateNursingHomeBody }
  >({
    mutationKey: ['UPDATE_NURSING_HOME_PHOENIX'],
    mutationFn: async ({ id, body }) => {
      const result = await api.updateNursingHome({
        params: { id },
        body: { data: body },
      });
      return result.data;
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: NURSING_HOME_PHOENIX_KEY(result.id),
        }),
        queryClient.invalidateQueries({ queryKey: NURSING_HOMES_KEY }),
        // User profile embeds payment settings (side-menu "payments disabled" flag).
        queryClient.invalidateQueries({
          queryKey: USER_PROFILE_KEY(userId ?? ''),
        }),
      ]);
    },
  });
};

export const useGetLegalEntities = (
  options?: Omit<
    UseQueryOptions<
      {
        meta: {
          total_count: number;
        };
        data: LegalEntity[];
      },
      unknown,
      {
        meta: {
          total_count: number;
        };
        data: LegalEntity[];
      }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    meta: {
      total_count: number;
    };
    data: LegalEntity[];
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    LEGAL_ENTITIES_KEY,
    (api) => {
      return api.getLegalEntities();
    },
    {
      ...options,
    }
  );
};

export const useGetLegalEntityIdvSession = (
  legalEntityId: string,
  options: { enabled: boolean }
): UseQueryResult<IdvSession, unknown> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    LEGAL_ENTITY_IDV_SESSION_KEY(legalEntityId),
    (api) => api.getLegalEntityIdvSession({ params: { legalEntityId } }),
    { enabled: options.enabled, select: (result) => result.data }
  );
};

export const useCreateLegalEntityIdvSession = (
  legalEntityId: string
): UseMutationResult<IdvSession, unknown, CreateIdvSessionBody> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['CREATE_LEGAL_ENTITY_IDV_SESSION', legalEntityId],
    mutationFn: async (body) =>
      (
        await api.createLegalEntityIdvSession({
          params: { legalEntityId },
          body,
        })
      ).data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: LEGAL_ENTITY_IDV_SESSION_KEY(legalEntityId),
        }),
        queryClient.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY }),
      ]);
    },
  });
};

export const useRetryLegalEntityIdvPsc = (
  legalEntityId: string
): UseMutationResult<IdvPsc, unknown, string> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['RETRY_LEGAL_ENTITY_IDV_PSC', legalEntityId],
    mutationFn: async (pscId) =>
      (await api.retryLegalEntityIdvPsc({ params: { legalEntityId, pscId } }))
        .data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: LEGAL_ENTITY_IDV_SESSION_KEY(legalEntityId),
      });
    },
  });
};
